import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Label, Wrapper } from './StaticField';
import { exceedsMaxLength } from './maxLength';
import { fieldDisplayName, humanizeKey, inferInputType, isRequiredField } from '../fillLayout';
import RequiredMark from './RequiredMark';

type Props = {
  item: any;
  name: string;
  value: string;
  onChange: Function;
  onBlur?: Function;
  wide?: boolean;
  shortText?: boolean;
  // Wiring for the Inspection Notes tab: focus registry, highlight-on-focus and
  // Enter-to-advance. All optional so other callers are unaffected.
  registerRef?: (key: string, el: HTMLElement | null) => void;
  onFocusField?: (key: string) => void;
  onAdvance?: (key: string) => void;
};

const TextField = ({
  item,
  name,
  value,
  onChange,
  onBlur = () => {},
  wide = false,
  shortText = false,
  registerRef,
  onFocusField,
  onAdvance,
}: Props) => {
  const { doubleWide, inputType, caption, max } = item;
  const [count, setCount] = useState<number>(0);
  // `caption` belongs to matrix cells; otherwise the field's own label wins,
  // falling back to the key for records created before labels existed.
  const captionText = caption ? humanizeKey(caption) : fieldDisplayName(name, item);

  useEffect(() => {
    if (value) {
      setCount(value.length);
    } else {
      setCount(0);
    }
  }, [value]);

  const onTextChanged = (e: any) => {
    if (exceedsMaxLength(e.target.value, max)) {
      return;
    }
    onChange(e);
  };

  const overLimit = !!max && count >= max;

  // A builder-set inputType always wins; otherwise infer a richer control from
  // the key. inferInputType refuses to upgrade a value a native picker would
  // wipe, so legacy text like '2:30 PM' stays editable.
  const inferred = inferInputType(name, item, value);
  const isTextArea = !inputType && inferred === 'textarea';
  const resolvedType = inputType || (inferred === 'textarea' ? 'text' : inferred);

  const handleFocus = () => {
    if (onFocusField) onFocusField(name);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter advances to the next field; never inside a textarea, where Enter is
    // a legitimate newline.
    if (e.key === 'Enter' && !isTextArea && onAdvance) {
      e.preventDefault();
      onAdvance(name);
    }
  };

  // Required and still blank: amber, like the on-page form.
  const missing = !caption && isRequiredField(name, item) && !`${value ?? ''}`.trim();

  const shared = {
    name,
    'data-missing': missing ? 'true' : undefined,
    'aria-required': !caption && isRequiredField(name, item),
    onChange: onTextChanged,
    onBlur: onBlur as any,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    value: value || '',
    autoComplete: 'off',
  };

  return (
    <FieldWrapper $doubleWide={!!doubleWide || isTextArea}>
      <Label wide={wide}>
        {captionText}
        {!caption && isRequiredField(name, item) && <RequiredMark />}
      </Label>
      <InputRow $stack={isTextArea}>
        {isTextArea ? (
          <TextArea
            ref={el => registerRef && registerRef(name, el)}
            rows={3}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...shared}
          />
        ) : (
          <Input
            ref={el => registerRef && registerRef(name, el)}
            $shortText={shortText}
            type={resolvedType}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...shared}
          />
        )}
        {!!max && (
          <Counter $warning={overLimit}>
            {count}/{max}
          </Counter>
        )}
      </InputRow>
    </FieldWrapper>
  );
};

export default TextField;

const FieldWrapper = styled(Wrapper)<{ $doubleWide: boolean }>`
  grid-column: ${({ $doubleWide }) => ($doubleWide ? '1 / -1' : 'auto')};
`;

const InputRow = styled.div<{ $stack: boolean }>`
  display: flex;
  align-items: ${({ $stack }) => ($stack ? 'flex-end' : 'center')};
  gap: 10px;
  width: 100%;
`;

const fieldStyles = `
  flex: 1;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--neutral-0);
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  font-size: 0.8125rem;
  color: var(--mainText);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;

  &::placeholder {
    color: var(--neutral-400);
  }

  &:hover {
    border-color: var(--neutral-400);
  }

  &[data-missing='true'] {
    border-color: var(--warning-500);
    background: var(--warning-50);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const Input = styled.input<{ $shortText: boolean }>`
  ${fieldStyles}
  width: ${({ $shortText }) => ($shortText ? '60px' : '100%')};
  height: 32px;

  &[type='time'],
  &[type='date'] {
    padding: 0 10px;
  }
`;

const TextArea = styled.textarea`
  ${fieldStyles}
  width: 100%;
  min-height: 64px;
  padding: 8px 10px;
  line-height: 1.45;
  resize: vertical;
`;

const Counter = styled.span<{ $warning: boolean }>`
  font-size: 0.75rem;
  color: ${({ $warning }) => ($warning ? 'var(--danger-500)' : 'var(--neutral-500)')};
  white-space: nowrap;
`;
